/* Login-free QR check-in page.
 * The per-event QR links here as /event-checkin?code=<checkinCode>.
 * The visitor enters their email (code pre-filled from the QR) and checks in with
 * NO login and NO account. If they aren't on the attendee list they can add
 * themselves as a guest by supplying their name. This page never calls an
 * auth-gated API and never redirects to /login. */

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ApiClient from '../../methods/api/apiClient';

type ResultKind = 'success' | 'invalid' | 'error' | null;

const EventCheckIn: React.FC = () => {
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState<string>('');
  const [code, setCode] = useState<string>((searchParams.get('code') || '').trim());
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');

  // Reveals the guest self-add step when the email isn't on the attendee list.
  const [needsName, setNeedsName] = useState<boolean>(false);
  const [connectMeetLabel, setConnectMeetLabel] = useState<string>('ConnectMeet');

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');

  const [result, setResult] = useState<ResultKind>(null);
  const [alreadyCheckedIn, setAlreadyCheckedIn] = useState<boolean>(false);
  const [eventName, setEventName] = useState<string>('');
  const [fullName, setFullName] = useState<string>('');

  const submit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setErrorMsg('');

    const trimmedEmail = email.trim();
    const trimmedCode = code.trim();
    if (!trimmedEmail || !trimmedCode) {
      setErrorMsg('Please enter both your email and the check-in code.');
      return;
    }
    if (needsName && (!firstName.trim() || !lastName.trim())) {
      setErrorMsg('Please enter your first and last name to check in as a guest.');
      return;
    }

    const payload: any = { email: trimmedEmail, code: trimmedCode };
    if (needsName) {
      payload.name = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
    }

    setSubmitting(true);
    try {
      const res: any = await ApiClient.post('api/checkin/by-code', payload);
      setSubmitting(false);

      if (res && res.connectMeetLabel) setConnectMeetLabel(res.connectMeetLabel);

      if (res && res.success) {
        setAlreadyCheckedIn(res.alreadyCheckedIn === true);
        setEventName(res.eventName || '');
        setFullName(res.fullName || '');
        setResult('success');
        return;
      }

      const reason = res && res.reason;
      if (reason === 'not_on_list') {
        // Not on the list yet — reveal the guest self-add step (not an error).
        setEventName(res.eventName || '');
        setNeedsName(true);
        return;
      }
      if (reason === 'invalid_code') {
        setResult('invalid');
        return;
      }
      // missing_fields or anything else
      setErrorMsg((res && res.message) || 'Something went wrong. Please try again.');
    } catch (err) {
      setSubmitting(false);
      setErrorMsg('Something went wrong. Please try again.');
    }
  };

  // ---- Success screen ----
  if (result === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md w-full">
          <div className="text-green-500 text-6xl mb-4">&#10003;</div>
          <h1 className="text-2xl font-bold mb-2">
            {alreadyCheckedIn ? "You're already checked in" : "You're checked in!"}
          </h1>
          <p className="text-gray-600 mb-2">
            {alreadyCheckedIn
              ? 'No need to do anything else — you are on the list.'
              : 'Thanks, you have been checked in.'}
          </p>
          {fullName ? <p className="font-semibold text-lg">{fullName}</p> : null}
          {eventName ? <p className="text-gray-500 text-sm mt-1">{eventName}</p> : null}
        </div>
      </div>
    );
  }

  // ---- Invalid code screen ----
  if (result === 'invalid') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md w-full">
          <div className="text-red-500 text-6xl mb-4">&#10007;</div>
          <h1 className="text-2xl font-bold mb-2">Check-in code not found</h1>
          <p className="text-gray-600 mb-4">
            That check-in code doesn't match an event. Please double-check the code and try again.
          </p>
          <button
            onClick={() => { setResult(null); setNeedsName(false); }}
            className="bg-orange-500 text-white px-6 py-3 rounded hover:bg-orange-600 w-full"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // ---- Form (email + code, plus guest name step) ----
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
        <h1 className="text-2xl font-bold mb-1 text-center">Event Check-In</h1>
        <p className="text-gray-500 text-sm mb-6 text-center">
          Enter your email and the check-in code to check in. No login needed.
        </p>

        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
          autoComplete="email"
        />

        <label className="block text-sm font-medium text-gray-700 mb-1">Check-in code</label>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="e.g. GWN-7K3P"
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 uppercase tracking-wider"
        />

        {needsName ? (
          <div className="mb-4 border-t pt-4">
            <p className="text-sm text-gray-700 mb-3">
              We couldn't find <span className="font-semibold">{email.trim()}</span> on the list for
              {eventName ? ' ' + eventName : ' this event'}. Add your name to check in as a guest.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First"
              className="w-full border border-gray-300 rounded px-3 py-2 mb-3"
              autoComplete="given-name"
            />
            <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last"
              className="w-full border border-gray-300 rounded px-3 py-2"
              autoComplete="family-name"
            />
          </div>
        ) : null}

        {errorMsg ? <div className="text-red-600 text-sm mb-3">{errorMsg}</div> : null}

        <button
          type="submit"
          disabled={submitting}
          className="bg-green-600 text-white px-6 py-3 rounded hover:bg-green-700 w-full disabled:opacity-60"
        >
          {submitting ? 'Checking in...' : needsName ? 'Check in as guest' : 'Check in'}
        </button>
      </form>
    </div>
  );
};

export default EventCheckIn;
